// Thème « Licorne » de la page Renouvellements : rose et mauve, paillettes,
// messages d'encouragement, et une licorne qui traverse l'écran chaque fois
// qu'un client est gardé (renew signé ou conservé en rétention).
//
// Le thème est un choix PAR UTILISATEUR (user_metadata.renouv_theme). Il se
// pose sur <html> tant que la page est montée, pour que le tiroir de fiche —
// rendu hors de l'arbre de la page — le prenne aussi.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '@/api/supabaseClient';
import './theme-licorne.css';

export const THEMES = { STANDARD: 'standard', LICORNE: 'licorne' };

const POLICES = 'https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Nunito:wght@400;600;700;800&display=swap';

const ENCOURAGEMENTS = [
  'Chaque client que tu gardes, c’est une petite victoire ✨',
  'Un appel à la fois, tu fais des merveilles 🌸',
  'Les licornes croient en toi. Nous aussi 🦄',
  'Tu transformes les impayés en paillettes 💖',
  'Pause thé méritée après le prochain dossier ☕🌷',
];
// Réaction du thème à chaque statut de suivi enregistré : une fête à la
// mesure du pas franchi, ou un petit orage quand le client ne veut plus.
export const REACTIONS = {
  contacte: {
    niveau: 'petite', duree: 2200,
    messages: ['Premier contact, bien joué ✨', 'C’est parti ! 🌸', 'Un pas de plus 💫'],
  },
  rdv_pris: {
    niveau: 'moyenne', duree: 3200,
    messages: ['Rendez-vous décroché ! 🌈', 'Il a dit oui au rendez-vous 💖', 'Trop forte, un RDV ! 🦄'],
  },
  renew_signe: {
    niveau: 'grande', duree: 4200,
    messages: ['RENEW SIGNÉ ! Tu es magique 🦄✨', 'Yesss, un de plus dans la famille 💖', 'Signé ! Les licornes dansent 🌈'],
  },
  conserve_retention: {
    niveau: 'grande', duree: 4200,
    messages: ['Client gardé ! Rétention réussie 🦄', 'Sauvé de justesse, bravo 💖', 'Il reste avec nous ✨ Magique !'],
  },
  non_interesse: {
    niveau: 'orage', duree: 4200,
    messages: ['Oh non… la licorne est triste 🥺', 'Pas grave, la prochaine sera la bonne 💜', 'Un petit orage, et le soleil revient 🌦️'],
  },
};

/** Réaction à un statut, ou null pour un statut neutre (« à contacter », aucun). */
export function reactionStatut(statut) {
  return REACTIONS[statut] || null;
}

const DELAI_ENCOURAGEMENT_MS = 6000;
const GERBE_CLIC = 10;
const FILANTE_MIN_MS = 18000;
const FILANTE_MAX_MS = 34000;
const DUREE_FILANTE_MS = 1500;
const FLOTTANTS = ['🌸', '☁️', '✨', '🌷', '☁️', '🌟'];

/** Fleurs, nuages et étoiles qui dérivent derrière le contenu. */
function Flottants() {
  const [items] = useState(() => FLOTTANTS.map((emoji, i) => ({
    i, emoji,
    left: `${(i * 97) % 100}%`,
    top: `${(i * 53 + 11) % 95}%`,
    taille: 18 + ((i * 7) % 18),
    duree: `${14 + ((i * 5) % 13)}s`,
    delai: `${-i * 1.7}s`,
  })));
  return (
    <div className="licorne-flottants" aria-hidden="true">
      {items.map((f) => (
        <span key={f.i} style={{ left: f.left, top: f.top, fontSize: f.taille, animationDuration: f.duree, animationDelay: f.delai }}>
          {f.emoji}
        </span>
      ))}
    </div>
  );
}
const NB_PAILLETTES = 16;
const NB_RAFALE = 70;

/** Le bandeau d'accueil seul (la scène — paillettes, fêtes — est posée par la coque de l'application). */
export function LicorneBandeau({ actif, prenom }) {
  const [message, setMessage] = useState(0);
  useEffect(() => {
    if (!actif) return undefined;
    const id = setInterval(() => setMessage((m) => (m + 1) % ENCOURAGEMENTS.length), DELAI_ENCOURAGEMENT_MS);
    return () => clearInterval(id);
  }, [actif]);
  if (!actif) return null;
  return (
    <div className="licorne-bandeau">
      <LicorneSvg className="licorne-bandeau__licorne" />
      <div className="min-w-0">
        <p className="licorne-bandeau__titre">Coucou <span>{prenom || 'toi'}</span> 🌸</p>
        <p className="licorne-bandeau__message" aria-live="polite" key={message}>{ENCOURAGEMENTS[message]}</p>
      </div>
      <div className="licorne-bandeau__guirlande hidden sm:flex" aria-hidden="true">
        <span>🌸</span><span>🦄</span><span>💖</span><span>🌷</span>
      </div>
    </div>
  );
}

/** Thème courant de l'utilisateur, et de quoi le changer (persisté sur son compte). */
export function useRenouvTheme(user) {
  const [theme, setThemeLocal] = useState(user?.renouv_theme || THEMES.STANDARD);
  useEffect(() => { setThemeLocal(user?.renouv_theme || THEMES.STANDARD); }, [user?.renouv_theme]);

  const setTheme = async (suivant) => {
    const precedent = theme;
    setThemeLocal(suivant);
    const { error } = await supabase.auth.updateUser({ data: { renouv_theme: suivant } });
    if (error) {
      console.error('renouv_theme non enregistré', error);
      setThemeLocal(precedent);
    }
  };
  // Le choix est offert aux administrateurs et à qui a déjà un thème attitré.
  const peutChoisir = !!user?.is_admin || !!user?.renouv_theme;
  return { theme, setTheme, peutChoisir };
}

export function ThemeSelector({ theme, setTheme }) {
  return (
    <div data-tuto="theme" role="group" aria-label="Thème du tableau" className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1">
      {[[THEMES.STANDARD, 'Standard'], [THEMES.LICORNE, '🦄 Licorne']].map(([v, l]) => (
        <button
          key={v}
          type="button"
          aria-pressed={theme === v}
          onClick={() => setTheme(v)}
          className={`rounded-full px-3 py-1.5 text-[12px] font-semibold transition ${
            theme === v ? 'bg-primary text-primary-foreground' : 'text-foreground/65 hover:text-foreground'
          }`}
          style={{ minHeight: 36 }}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function LicorneSvg({ triste = false, ...props }) {
  return (
    <svg viewBox="-4 -6 128 108" aria-hidden="true" {...props}>
      <defs>
        <linearGradient id="licorne-criniere" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FF5FAE" /><stop offset=".5" stopColor="#A974D6" /><stop offset="1" stopColor="#7FD1FF" />
        </linearGradient>
        <linearGradient id="licorne-corne" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFE08A" /><stop offset="1" stopColor="#FFB74D" />
        </linearGradient>
      </defs>
      <path d="M22 70 Q18 52 34 44 Q44 22 66 24 L78 10 L80 30 Q96 36 98 52 Q100 60 92 62 Q84 58 80 62 L78 84 Q76 90 70 90 L68 76 Q56 80 46 76 L44 90 Q38 92 36 86 L36 76 Q24 78 22 70 Z" fill="#FFFFFF" stroke="#E7C3DD" strokeWidth="2" />
      <path d="M78 10 L86 -2 L82 26 Z" fill="url(#licorne-corne)" />
      <path d="M60 26 Q46 20 40 34 Q34 44 26 46 Q40 50 50 40 Q58 34 66 30 Z" fill="url(#licorne-criniere)" />
      <path d="M22 68 Q6 64 8 80 Q14 74 22 76 Z" fill="url(#licorne-criniere)" />
      {triste ? (
        <>
          {/* Œil baissé et une larme. */}
          <path d="M84 47 Q88 44 92 47" stroke="#4A2545" strokeWidth="2.2" fill="none" strokeLinecap="round" />
          <path className="licorne-larme" d="M90 50 Q88 55 90 57 Q92 55 90 50 Z" fill="#7FD1FF" />
        </>
      ) : (
        <>
          <circle cx="88" cy="46" r="3" fill="#4A2545" />
          <circle cx="90" cy="54" r="3.2" fill="#FFB3D6" />
        </>
      )}
    </svg>
  );
}

const COULEURS_ARC = ['#FF5FAE', '#FF9F5F', '#FFD84D', '#6BDB9B', '#7FD1FF', '#A974D6'];

function ArcEnCiel() {
  return (
    <svg className="licorne-arc" viewBox="0 0 240 130" aria-hidden="true">
      {COULEURS_ARC.map((c, i) => (
        <path key={c} d={`M ${20 + i * 9} 125 A ${100 - i * 9} ${100 - i * 9} 0 0 1 ${220 - i * 9} 125`} stroke={c} strokeWidth="9" fill="none" strokeLinecap="round" pathLength="1" style={{ animationDelay: `${i * 70}ms` }} />
      ))}
    </svg>
  );
}

function Orage() {
  return (
    <div className="licorne-orage" aria-hidden="true">
      <div className="licorne-orage__pluie" />
      <div className="licorne-orage__flash" />
      {[18, 72].map((x, i) => (
        <svg key={x} className="licorne-orage__eclair" style={{ left: `${x}%`, animationDelay: `${300 + i * 900}ms` }} viewBox="0 0 40 90">
          <path d="M24 0 L6 48 L20 48 L10 90 L36 36 L22 36 L32 0 Z" fill="#FFE27A" stroke="#FFF6C7" strokeWidth="2" />
        </svg>
      ))}
      <div className="licorne-triste">
        <svg className="licorne-triste__nuage" viewBox="0 0 120 60">
          <path d="M28 44 Q10 44 12 30 Q14 18 28 20 Q32 6 50 8 Q64 0 76 12 Q96 8 100 26 Q114 28 110 40 Q108 46 96 46 Z" fill="#8B8BA7" />
        </svg>
        <LicorneSvg triste className="licorne-triste__corps" />
      </div>
    </div>
  );
}

/** L'animation d'une réaction ; démontée à la fin de sa durée. */
const DUREE_CHEVAUCHEE_MS = 2600;
const EPAISSEUR_BANDE = 14;
const SEMIS_MS = 90;

/**
 * La grande fête : un arc-en-ciel se dessine d'un bord à l'autre de l'écran
 * et une licorne le chevauche en semant des paillettes.
 */
function ChevaucheeArcEnCiel({ rafale }) {
  const tracesRef = useRef([]);
  const licorneRef = useRef(null);
  const [{ w, h }] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const chemin = (decalage) => `M -140 ${h * 0.82 + decalage} Q ${w / 2} ${-h * 0.28 + decalage} ${w + 140} ${h * 0.82 + decalage}`;

  useEffect(() => {
    const guide = tracesRef.current[0];
    if (!guide || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const longueur = guide.getTotalLength();
    const debut = performance.now();
    let dernierSemis = 0;
    let raf = 0;
    const pas = (maintenant) => {
      const p = Math.min(1, (maintenant - debut) / DUREE_CHEVAUCHEE_MS);
      const facile = 1 - (1 - p) ** 2;
      tracesRef.current.forEach((t) => { if (t) t.style.strokeDashoffset = String(1 - facile); });
      const pt = guide.getPointAtLength(facile * longueur);
      const avant = guide.getPointAtLength(Math.min(longueur, facile * longueur + 2));
      const angle = (Math.atan2(avant.y - pt.y, avant.x - pt.x) * 180) / Math.PI;
      if (licorneRef.current) {
        licorneRef.current.style.transform = `translate(${pt.x - 85}px, ${pt.y - 130}px) rotate(${angle}deg)`;
      }
      if (maintenant - dernierSemis > SEMIS_MS) {
        rafale.current(pt.x, pt.y - 20, 14);
        dernierSemis = maintenant;
      }
      if (p < 1) raf = requestAnimationFrame(pas);
    };
    raf = requestAnimationFrame(pas);
    return () => cancelAnimationFrame(raf);
  }, [rafale]);

  return (
    <div className="licorne-chevauchee" aria-hidden="true">
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        {COULEURS_ARC.map((c, i) => (
          <path
            key={c}
            ref={(el) => { tracesRef.current[i] = el; }}
            d={chemin(i * EPAISSEUR_BANDE)}
            stroke={c}
            strokeWidth={EPAISSEUR_BANDE + 1}
            fill="none"
            pathLength="1"
            style={{ strokeDasharray: 1, strokeDashoffset: 1 }}
          />
        ))}
      </svg>
      <div ref={licorneRef} className="licorne-chevauchee__cavaliere">
        <LicorneSvg />
      </div>
    </div>
  );
}

const ENVOLEE = ['💖', '🌸', '🦄', '✨', '🌷', '💜', '⭐', '🌈'];
const NB_ENVOLEE = 28;

/** Pluie inversée : cœurs, fleurs et licornes qui s'envolent du bas de l'écran. */
function Envolee() {
  const [bulles] = useState(() => Array.from({ length: NB_ENVOLEE }, (_, i) => ({
    i,
    emoji: ENVOLEE[i % ENVOLEE.length],
    gauche: Math.random() * 100,
    delai: Math.random() * 1200,
    duree: 2200 + Math.random() * 1400,
    taille: 18 + Math.random() * 22,
    derive: (Math.random() - 0.5) * 120,
  })));
  return (
    <div className="licorne-envolee" aria-hidden="true">
      {bulles.map((b) => (
        <span
          key={b.i}
          style={{
            left: `${b.gauche}%`, fontSize: b.taille, animationDelay: `${b.delai}ms`,
            animationDuration: `${b.duree}ms`, '--derive': `${b.derive}px`,
          }}
        >
          {b.emoji}
        </span>
      ))}
    </div>
  );
}

/** L'animation d'une réaction ; démontée à la fin de sa durée. */
function Fete({ niveau, rafale }) {
  if (niveau === 'orage') return <Orage />;
  if (niveau === 'grande') {
    return (
      <>
        <ChevaucheeArcEnCiel rafale={rafale} />
        <Envolee />
      </>
    );
  }
  return niveau === 'moyenne' ? <ArcEnCiel /> : null;
}

/** Paillettes ambiantes + rafales, sur un canvas fixe qui ne capte aucun clic. */
function usePaillettes(canvasRef, actif) {
  const rafaleRef = useRef(() => {});
  useEffect(() => {
    const cv = canvasRef.current;
    if (!actif || !cv) return undefined;
    const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ctx = cv.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const couleurs = ['#FF5FAE', '#A974D6', '#FFC94D', '#FFFFFF', '#7FD1FF'];
    let W = 0; let H = 0; let grains = []; let raf = 0;
    const taille = () => { W = cv.width = window.innerWidth * dpr; H = cv.height = window.innerHeight * dpr; };
    const grain = (x, y, rafale) => ({
      x: x ?? Math.random() * W, y: y ?? Math.random() * H, r: (Math.random() * 2 + 1) * dpr,
      vx: rafale ? (Math.random() - 0.5) * 9 : (Math.random() - 0.5) * 0.25,
      vy: rafale ? (Math.random() - 0.8) * 9 : Math.random() * 0.35 + 0.1,
      vie: rafale ? 90 : Infinity, c: couleurs[Math.floor(Math.random() * couleurs.length)], t: Math.random() * 6,
    });
    const etoile = (g) => {
      const a = 0.45 + 0.55 * Math.abs(Math.sin(g.t));
      ctx.globalAlpha = Number.isFinite(g.vie) ? Math.min(1, g.vie / 40) * a : a * 0.35;
      ctx.fillStyle = g.c;
      ctx.beginPath();
      for (let k = 0; k < 4; k += 1) {
        const ang = (k * Math.PI) / 2;
        ctx.lineTo(g.x + Math.cos(ang) * g.r * 2.4, g.y + Math.sin(ang) * g.r * 2.4);
        ctx.lineTo(g.x + Math.cos(ang + Math.PI / 4) * g.r * 0.7, g.y + Math.sin(ang + Math.PI / 4) * g.r * 0.7);
      }
      ctx.closePath();
      ctx.fill();
    };
    const boucle = () => {
      ctx.clearRect(0, 0, W, H);
      grains = grains.filter((g) => g.vie > 0);
      for (const g of grains) {
        g.x += g.vx; g.y += g.vy; g.t += 0.05;
        if (Number.isFinite(g.vie)) { g.vie -= 1; g.vy += 0.18; } else if (g.y > H + 10) { g.y = -10; g.x = Math.random() * W; }
        etoile(g);
      }
      raf = requestAnimationFrame(boucle);
    };
    taille();
    window.addEventListener('resize', taille);
    grains = Array.from({ length: NB_PAILLETTES }, () => grain());
    if (reduit) grains.forEach(etoile); else boucle();
    rafaleRef.current = (x, y, nombre = NB_RAFALE) => {
      if (reduit) return;
      for (let i = 0; i < nombre; i += 1) grains.push(grain(x * dpr, y * dpr, true));
    };
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', taille); rafaleRef.current = () => {}; };
  }, [canvasRef, actif]);
  return rafaleRef;
}

/**
 * Décor et bandeau du thème Licorne. Ne rend rien en thème standard.
 * `prenom` personnalise l'accueil.
 */
export function LicorneDecor({ actif, prenom, bandeau = true }) {
  const canvasRef = useRef(null);
  const rafale = usePaillettes(canvasRef, actif);
  const [message, setMessage] = useState(0);
  // Réaction en cours : { niveau, message, cle } — `cle` relance l'animation.
  const [fete, setFete] = useState(null);

  // Le thème se pose sur <html> : le tiroir de fiche (portail) le prend aussi.
  useEffect(() => {
    if (!actif) return undefined;
    document.documentElement.classList.add('theme-licorne');
    if (!document.querySelector(`link[href="${POLICES}"]`)) {
      const lien = document.createElement('link');
      lien.rel = 'stylesheet';
      lien.href = POLICES;
      document.head.appendChild(lien);
    }
    return () => document.documentElement.classList.remove('theme-licorne');
  }, [actif]);

  useEffect(() => {
    if (!actif) return undefined;
    const id = setInterval(() => setMessage((m) => (m + 1) % ENCOURAGEMENTS.length), DELAI_ENCOURAGEMENT_MS);
    return () => clearInterval(id);
  }, [actif]);

  // Petite gerbe au clic sur un bouton d'action (pas de traînée sous la souris : elle fatiguait l'œil).
  useEffect(() => {
    if (!actif || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const surClic = (e) => {
      if (e.target.closest?.('button.bg-primary, button[type="submit"], [data-fete]')) rafale.current(e.clientX, e.clientY, GERBE_CLIC);
    };
    window.addEventListener('pointerdown', surClic, { passive: true });
    return () => window.removeEventListener('pointerdown', surClic);
  }, [actif, rafale]);

  // Une étoile filante de temps en temps.
  const [filante, setFilante] = useState(0);
  useEffect(() => {
    if (!actif) return undefined;
    let minuterie;
    const prochaine = () => {
      minuterie = setTimeout(() => {
        setFilante(Date.now());
        setTimeout(() => setFilante(0), DUREE_FILANTE_MS);
        prochaine();
      }, FILANTE_MIN_MS + Math.random() * (FILANTE_MAX_MS - FILANTE_MIN_MS));
    };
    prochaine();
    return () => clearTimeout(minuterie);
  }, [actif]);

  // Un statut enregistré : fête à la mesure du pas franchi, ou orage.
  useEffect(() => {
    if (!actif) return undefined;
    let minuterie;
    const salves = [];
    const surStatut = (e) => {
      const reaction = reactionStatut(e.detail?.statut);
      if (!reaction) return;
      const message = reaction.messages[Math.floor(Math.random() * reaction.messages.length)];
      setFete({ niveau: reaction.niveau, message, cle: Date.now() });
      const { innerWidth: w, innerHeight: h } = window;
      if (reaction.niveau !== 'orage') rafale.current(w / 2, h / 3);
      if (reaction.niveau === 'grande') {
        salves.push(setTimeout(() => rafale.current(w * 0.2, h * 0.5), 300));
        salves.push(setTimeout(() => rafale.current(w * 0.8, h * 0.5), 600));
      }
      clearTimeout(minuterie);
      minuterie = setTimeout(() => setFete(null), reaction.duree);
    };
    window.addEventListener('renouv:statut', surStatut);
    return () => {
      window.removeEventListener('renouv:statut', surStatut);
      clearTimeout(minuterie);
      salves.forEach(clearTimeout);
    };
  }, [actif, rafale]);

  if (!actif) return null;
  // Paillettes, fêtes et orage vont dans un portail : dans l'arbre de la page,
  // ils héritaient de son niveau d'empilement et passaient sous la barre de
  // filtres et sous le tiroir de fiche.
  const scene = createPortal(
    <>
      <Flottants />
      <canvas ref={canvasRef} className="licorne-paillettes" aria-hidden="true" />
      {filante > 0 && <div key={filante} className="licorne-filante" aria-hidden="true" />}
      {fete && <Fete key={fete.cle} niveau={fete.niveau} rafale={rafale} />}
      <div
        role="status"
        aria-live="polite"
        className={`licorne-bravo ${fete ? 'is-visible' : ''} ${fete?.niveau === 'orage' ? 'is-orage' : ''} ${fete?.niveau === 'grande' ? 'is-grande' : ''}`}
      >
        {fete?.message}
      </div>
    </>,
    document.body,
  );
  if (!bandeau) return scene;
  return (
    <>
      {scene}
      <div className="licorne-bandeau">
        <LicorneSvg className="licorne-bandeau__licorne" />
        <div className="min-w-0">
          <p className="licorne-bandeau__titre">Coucou <span>{prenom || 'toi'}</span> 🌸</p>
          <p className="licorne-bandeau__message" aria-live="polite" key={message}>{ENCOURAGEMENTS[message]}</p>
        </div>
        <div className="licorne-bandeau__guirlande hidden sm:flex" aria-hidden="true">
          <span>🌸</span><span>🦄</span><span>💖</span><span>🌷</span>
        </div>
      </div>
    </>
  );
}
